import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from './adminApi';
import { STATUS_META, StatusPill, TierPill } from './AdminDashboard';
import {
  Trash2,
  Mail,
  Building2,
  Search,
  X,
  Save,
  Calendar,
  Download,
  Upload,
  CheckSquare,
  Square,
  Filter,
  ArrowUpDown,
  Flame,
} from 'lucide-react';

const STATUSES = ['new', 'contacted', 'qualified', 'won', 'lost'];

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sortByScore, setSortByScore] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importInputRef = React.useRef(null);

  const handleImportCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await adminApi.importContactsCsv(file);
      setImportResult({ kind: 'success', ...result });
      refresh();
    } catch (err) {
      setImportResult({
        kind: 'error',
        message: err?.response?.data?.detail || err.message || 'Import failed.',
      });
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const refresh = () => {
    setLoading(true);
    adminApi
      .listContacts()
      .then((data) => setContacts(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    let list = contacts;
    if (filter !== 'all') list = list.filter((c) => c.status === filter);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (c) =>
          (c.firstName || '').toLowerCase().includes(q) ||
          (c.lastName || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q) ||
          (c.company || '').toLowerCase().includes(q) ||
          (c.message || '').toLowerCase().includes(q)
      );
    }
    if (sortByScore) {
      list = [...list].sort((a, b) => (b.score || 0) - (a.score || 0));
    }
    return list;
  }, [contacts, filter, query, sortByScore]);

  const handleStatusChange = async (id, newStatus) => {
    const updated = await adminApi.updateContact(id, { status: newStatus });
    setContacts((prev) => prev.map((c) => (c.id === id ? updated : c)));
    if (selected?.id === id) setSelected(updated);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    await adminApi.deleteContact(id);
    setContacts((prev) => prev.filter((c) => c.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  const handleSaveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      const updated = await adminApi.updateContact(selected.id, { notes: selected.notes || '' });
      setContacts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      setSelected(updated);
    } finally {
      setSavingNotes(false);
    }
  };

  const counts = useMemo(() => {
    const out = { all: contacts.length };
    STATUSES.forEach((s) => (out[s] = contacts.filter((c) => c.status === s).length));
    return out;
  }, [contacts]);

  const toggleSelect = (id) => {
    const next = new Set(bulkSelected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setBulkSelected(next);
  };

  const toggleSelectAll = () => {
    if (bulkSelected.size === filtered.length) {
      setBulkSelected(new Set());
    } else {
      setBulkSelected(new Set(filtered.map((c) => c.id)));
    }
  };

  const handleBulkStatus = async (status) => {
    if (bulkSelected.size === 0) return;
    setBulkBusy(true);
    try {
      await adminApi.bulkContacts({ ids: [...bulkSelected], status });
      refresh();
      setBulkSelected(new Set());
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    if (bulkSelected.size === 0) return;
    if (!window.confirm(`Delete ${bulkSelected.size} leads permanently?`)) return;
    setBulkBusy(true);
    try {
      await adminApi.bulkContacts({ ids: [...bulkSelected], delete: true });
      refresh();
      setBulkSelected(new Set());
      if (selected && bulkSelected.has(selected.id)) setSelected(null);
    } finally {
      setBulkBusy(false);
    }
  };

  const handleExportCsv = async () => {
    const blob = await adminApi.exportContactsCsv();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `grouth-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="px-8 lg:px-12 py-10" data-testid="admin-contacts-page">
      <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-['Inter'] mb-2">CRM</p>
          <h1 className="text-white text-4xl font-light font-['Inter']">Leads</h1>
          <p className="text-white/40 text-sm font-['Lato'] mt-2">
            Manage every contact-form submission. Filter, status-track, bulk-update, and export.
          </p>
        </div>
        <button
          onClick={handleExportCsv}
          disabled={contacts.length === 0}
          data-testid="contacts-export-csv"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 text-white/80 text-sm font-['Inter'] hover:bg-white/5 transition disabled:opacity-40"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </header>

      {/* CSV import row */}
      <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="contacts-import-row">
        <input
          ref={importInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => handleImportCsv(e.target.files?.[0])}
          className="hidden"
          data-testid="contacts-import-input"
        />
        <button
          type="button"
          onClick={() => importInputRef.current?.click()}
          disabled={importing}
          data-testid="contacts-import-csv"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 text-xs font-['Inter'] hover:bg-white/10 transition disabled:opacity-50"
        >
          <Upload className="w-3.5 h-3.5" />
          {importing ? 'Importing…' : 'Import CSV'}
        </button>
        <span className="text-white/30 text-xs font-['Lato']">
          Header row required. Columns: <span className="font-mono">email, firstName, lastName, company, service, message, source, status</span>. Duplicate emails are skipped.
        </span>
      </div>

      {importResult && (
        <div
          data-testid="contacts-import-result"
          className={`mb-5 px-4 py-3 rounded-lg text-xs font-['Lato'] flex items-start gap-2 ${
            importResult.kind === 'success'
              ? 'bg-[#83BF42]/10 border border-[#83BF42]/20 text-[#83BF42]'
              : 'bg-red-500/10 border border-red-500/20 text-red-300'
          }`}
        >
          {importResult.kind === 'success' ? (
            <span>
              Imported <strong>{importResult.imported}</strong> · Skipped duplicates: {importResult.skipped_duplicates} · Skipped invalid: {importResult.skipped_invalid}
              {importResult.errors?.length ? ` · First issues: ${importResult.errors.slice(0, 2).join(' ')}` : ''}
            </span>
          ) : (
            <span>{importResult.message}</span>
          )}
          <button
            onClick={() => setImportResult(null)}
            className="ml-auto text-white/40 hover:text-white"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-2">
          {[['all', 'All'], ...STATUSES.map((s) => [s, STATUS_META[s].label])].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              data-testid={`contacts-filter-${key}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-['Inter'] transition-all ${
                filter === key
                  ? 'bg-[#83BF42] text-[#0F0F11]'
                  : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
              }`}
            >
              {label} <span className="ml-1 opacity-60">{counts[key] ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="relative w-full lg:w-72">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search leads…"
            data-testid="contacts-search"
            className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-white placeholder-white/30 font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
          />
        </div>
      </div>

      {/* Bulk action bar */}
      {bulkSelected.size > 0 && (
        <div
          data-testid="contacts-bulk-bar"
          className="bg-[#83BF42]/10 border border-[#83BF42]/30 rounded-xl p-3 mb-4 flex items-center gap-3 flex-wrap"
        >
          <span className="text-[#83BF42] text-sm font-['Inter'] font-medium px-2">
            {bulkSelected.size} selected
          </span>
          <span className="text-white/30 text-xs font-['Inter']">·</span>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => handleBulkStatus(s)}
              disabled={bulkBusy}
              data-testid={`bulk-status-${s}`}
              className="px-3 py-1.5 rounded-full text-xs font-['Inter'] bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition disabled:opacity-50"
            >
              Mark {STATUS_META[s].label}
            </button>
          ))}
          <button
            onClick={handleBulkDelete}
            disabled={bulkBusy}
            data-testid="bulk-delete"
            className="px-3 py-1.5 rounded-full text-xs font-['Inter'] border border-red-500/30 text-red-300 hover:bg-red-500/10 transition ml-auto disabled:opacity-50"
          >
            Delete selected
          </button>
          <button
            onClick={() => setBulkSelected(new Set())}
            data-testid="bulk-clear"
            className="text-white/40 hover:text-white p-1.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* List */}
        <div className="lg:col-span-7 bg-[#15151A] border border-white/5 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-white/40 text-sm font-['Lato'] py-16 text-center" data-testid="contacts-empty">
              No leads match the current filters.
            </p>
          ) : (
            <>
              {/* Select-all bar */}
              <div className="px-5 py-2.5 border-b border-white/5 flex items-center gap-3">
                <button
                  onClick={toggleSelectAll}
                  data-testid="contacts-select-all"
                  className="text-white/60 hover:text-white"
                  aria-label="Select all"
                >
                  {bulkSelected.size === filtered.length && filtered.length > 0 ? (
                    <CheckSquare className="w-4 h-4 text-[#83BF42]" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                </button>
                <span className="text-white/40 text-xs font-['Inter'] uppercase tracking-wider">
                  {bulkSelected.size === 0
                    ? `${filtered.length} ${filtered.length === 1 ? 'lead' : 'leads'}`
                    : `${bulkSelected.size} of ${filtered.length} selected`}
                </span>
                <button
                  onClick={() => setSortByScore((s) => !s)}
                  data-testid="contacts-sort-toggle"
                  className={`ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-['Inter'] border transition ${
                    sortByScore
                      ? 'bg-[#83BF42]/15 text-[#83BF42] border-[#83BF42]/30'
                      : 'bg-white/5 text-white/50 border-white/10 hover:text-white'
                  }`}
                  title="Sort by lead score"
                >
                  {sortByScore ? <Flame className="w-3 h-3" /> : <ArrowUpDown className="w-3 h-3" />}
                  {sortByScore ? 'By score' : 'Newest first'}
                </button>
              </div>
              <ul className="divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
                {filtered.map((c) => (
                  <li
                    key={c.id}
                    data-testid={`contact-row-${c.id}`}
                    className={`p-5 transition-colors ${
                      selected?.id === c.id ? 'bg-white/[0.04]' : 'hover:bg-white/[0.02]'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleSelect(c.id); }}
                        data-testid={`contact-select-${c.id}`}
                        className="mt-0.5 text-white/50 hover:text-white shrink-0"
                        aria-label={`Select ${c.firstName} ${c.lastName}`}
                      >
                        {bulkSelected.has(c.id) ? (
                          <CheckSquare className="w-4 h-4 text-[#83BF42]" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => setSelected(c)}
                        className="text-left flex-1 min-w-0"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm font-['Inter'] truncate flex items-center gap-2">
                              {c.firstName} {c.lastName}
                              {c.score_tier && c.score_tier !== 'cold' && (
                                <TierPill tier={c.score_tier} score={c.score} />
                              )}
                            </p>
                            <p className="text-white/40 text-xs font-['Lato'] truncate flex items-center gap-1.5 mt-1">
                              <Building2 className="w-3 h-3" /> {c.company}
                            </p>
                            <p className="text-white/40 text-xs font-['Lato'] truncate flex items-center gap-1.5 mt-0.5">
                              <Mail className="w-3 h-3" /> {c.email}
                            </p>
                          </div>
                          <StatusPill status={c.status} />
                        </div>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-5">
          {!selected ? (
            <div className="bg-[#15151A] border border-white/5 rounded-2xl p-10 text-center" data-testid="contacts-detail-empty">
              <p className="text-white/40 text-sm font-['Lato']">
                Select a lead to view details and manage status.
              </p>
            </div>
          ) : (
            <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6 space-y-6 sticky top-6" data-testid={`contact-detail-${selected.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-white text-xl font-medium font-['Inter'] flex items-center gap-2">
                    {selected.firstName} {selected.lastName}
                    {selected.score_tier && (
                      <TierPill tier={selected.score_tier} score={selected.score} />
                    )}
                  </h3>
                  <p className="text-white/50 text-sm font-['Lato']">{selected.company}</p>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  data-testid="contact-detail-close"
                  className="text-white/40 hover:text-white p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-2 text-sm font-['Lato']">
                <a
                  href={`mailto:${selected.email}`}
                  className="flex items-center gap-2 text-white/70 hover:text-[#83BF42]"
                >
                  <Mail className="w-3.5 h-3.5" /> {selected.email}
                </a>
                {selected.service && (
                  <p className="text-white/50">
                    <span className="text-white/40 text-xs uppercase tracking-wider mr-2">Service</span>
                    {selected.service}
                  </p>
                )}
                {selected.source && (
                  <p className="text-white/50" data-testid="contact-source">
                    <span className="text-white/40 text-xs uppercase tracking-wider mr-2">Source</span>
                    {selected.source}
                  </p>
                )}
                <p className="flex items-center gap-2 text-white/50 text-xs">
                  <Calendar className="w-3 h-3" />
                  {new Date(selected.created_at).toLocaleString()}
                </p>
              </div>

              {selected.message && (
                <div className="bg-white/[0.03] rounded-lg p-4">
                  <p className="text-white/40 text-xs uppercase tracking-wider font-['Inter'] mb-2">
                    Message
                  </p>
                  <p className="text-white/80 text-sm font-['Lato'] leading-relaxed whitespace-pre-wrap">
                    {selected.message}
                  </p>
                </div>
              )}

              {/* Status switcher */}
              <div>
                <p className="text-white/40 text-xs uppercase tracking-wider font-['Inter'] mb-3">
                  Status
                </p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(selected.id, s)}
                      data-testid={`contact-status-${s}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-['Inter'] border transition-all ${
                        selected.status === s
                          ? STATUS_META[s].color
                          : 'border-white/10 text-white/40 hover:text-white hover:border-white/30'
                      }`}
                    >
                      {STATUS_META[s].label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Note timeline */}
              <div data-testid="contact-notes-timeline">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-white/40 text-xs uppercase tracking-wider font-['Inter']">
                    Activity ({(selected.notes_history || []).length})
                  </p>
                </div>

                <div className="space-y-2 mb-3 max-h-64 overflow-y-auto pr-1">
                  {(selected.notes_history || []).slice().reverse().map((n) => (
                    <div
                      key={n.id}
                      data-testid={`note-${n.id}`}
                      className="bg-white/[0.03] border border-white/5 rounded-lg p-3 group"
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <span className="text-[#83BF42] text-[10px] uppercase tracking-wider font-['Inter']">
                          {n.author_email?.split('@')[0] || 'admin'}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-white/30 text-[10px] font-['Lato']">
                            {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                          </span>
                          <button
                            onClick={async () => {
                              await adminApi.deleteContactNote(selected.id, n.id);
                              const updated = await adminApi.getContact(selected.id);
                              setContacts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
                              setSelected(updated);
                            }}
                            className="text-white/30 hover:text-red-400 opacity-0 group-hover:opacity-100 transition"
                            data-testid={`note-delete-${n.id}`}
                            aria-label="Delete note"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <p className="text-white/80 text-sm font-['Lato'] whitespace-pre-wrap">{n.text}</p>
                    </div>
                  ))}
                  {(selected.notes_history || []).length === 0 && (
                    <p className="text-white/30 text-xs font-['Lato'] text-center py-4">
                      No activity yet. Add a note to start a thread.
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <input
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note…"
                    data-testid="contact-add-note-input"
                    className="flex-1 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm font-['Lato'] focus:outline-none focus:border-[#83BF42] transition-colors"
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && !e.shiftKey && newNote.trim() && !addingNote) {
                        e.preventDefault();
                        setAddingNote(true);
                        try {
                          const updated = await adminApi.addContactNote(selected.id, newNote.trim());
                          setContacts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
                          setSelected(updated);
                          setNewNote('');
                        } finally {
                          setAddingNote(false);
                        }
                      }
                    }}
                  />
                  <button
                    onClick={async () => {
                      if (!newNote.trim()) return;
                      setAddingNote(true);
                      try {
                        const updated = await adminApi.addContactNote(selected.id, newNote.trim());
                        setContacts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
                        setSelected(updated);
                        setNewNote('');
                      } finally {
                        setAddingNote(false);
                      }
                    }}
                    disabled={addingNote || !newNote.trim()}
                    data-testid="contact-add-note-button"
                    className="px-4 py-2 rounded-lg bg-[#83BF42] text-[#0F0F11] text-xs font-medium font-['Inter'] hover:bg-[#A8D96C] transition disabled:opacity-50"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Notes (legacy single-field) */}
              <div>
                <p className="text-white/40 text-xs uppercase tracking-wider font-['Inter'] mb-2">
                  Pinned summary
                </p>
                <textarea
                  rows={4}
                  value={selected.notes || ''}
                  onChange={(e) => setSelected({ ...selected, notes: e.target.value })}
                  data-testid="contact-notes"
                  placeholder="Add notes only the team can see…"
                  className="w-full px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/10 text-white text-sm font-['Lato'] focus:outline-none focus:border-[#83BF42] transition-colors resize-none"
                />
                <button
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                  data-testid="contact-save-notes"
                  className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#83BF42] text-[#0F0F11] text-xs font-medium font-['Inter'] hover:bg-[#A8D96C] transition disabled:opacity-50"
                >
                  <Save className="w-3.5 h-3.5" /> {savingNotes ? 'Saving…' : 'Save notes'}
                </button>
              </div>

              <button
                onClick={() => handleDelete(selected.id)}
                data-testid="contact-delete"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-red-500/20 text-red-400 text-xs font-['Inter'] hover:bg-red-500/10 transition"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete lead
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminContacts;
